import React, { useState } from 'react';
import { compose } from 'redux';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, MapPin, Calendar, Users, Shield } from 'lucide-react';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import { Page, LayoutSingleColumn } from '../../components';
import { injectIntl } from '../../util/reactIntl';
import homeImage from './home.jpg';
import locationsData from './Locations_New.json';
import DynamicPageListings from './DynamicPageListings';
import css from './DynamicPage.module.css';
import classNames from 'classnames';

const SITE_URL = 'https://wetrentals.com';

const formatSegment = segment =>
  segment
    // Insert a space before each capital letter that follows a lowercase letter ("BritishColumbia" -> "British Columbia")
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // Insert a space before a capital that precedes another capital + lowercase ("USA" stays, "NewZealand" -> "New Zealand")
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');

const getLocationSegments = pathname =>
  pathname
    .split('/')
    .filter(Boolean)
    .filter(
      segment =>
        segment.toLowerCase() !== 'destinations' && segment.toLowerCase() !== 'boat-rentals'
    );

const Breadcrumb = ({ locationSegments }) => {
  const generateUrl = index => {
    const path = locationSegments.slice(0, index + 1).join('/');
    return `/destinations/${path.toLowerCase()}/boat-rentals`;
  };

  return (
    <nav aria-label="breadcrumb" className={css.breadcrumb}>
      <ol className={css.breadcrumbList}>
        <li>
          <Link to="/" className={css.breadcrumbLink}>
            Home
          </Link>
        </li>
        {locationSegments.map((segment, index) => (
          <React.Fragment key={index}>
            <li className={css.breadcrumbSeparator}>/</li>
            <li>
              <Link
                to={generateUrl(index)}
                className={classNames(css.breadcrumbLink, {
                  [css.active]: index === locationSegments.length - 1,
                })}
              >
                {formatSegment(segment)}
              </Link>
            </li>
          </React.Fragment>
        ))}
      </ol>
    </nav>
  );
};

const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className={css.faqItem}>
    <button onClick={onToggle} className={css.faqQuestion}>
      <span className={css.faqQuestionText}>{question}</span>
      <ChevronDown className={classNames(css.faqIcon, { [css.faqIconRotated]: isOpen })} />
    </button>
    {isOpen && (
      <div className={css.faqAnswer}>
        <p className={css.faqAnswerText}>{answer}</p>
      </div>
    )}
  </div>
);

const buildFaqData = locationName => [
  {
    question: `How do I book a boat rental in ${locationName}?`,
    answer: `Browse the available boats in ${locationName} on WetRentals, pick the dates and times that work for you, and book online in minutes. You'll receive instant confirmation and the host's contact details.`,
  },
  {
    question: 'What is the cancellation policy?',
    answer:
      'You can generally cancel up to 72 hours before the rental for a full refund. Each host can set their own policy, so check the listing for the exact terms.',
  },
  {
    question: `Do I need a boating license to rent a boat in ${locationName}?`,
    answer: `License requirements depend on the boat and local rules in ${locationName}. Many rentals don't require a license, and where one is required your host will let you know during booking.`,
  },
  {
    question: 'Are life jackets and safety equipment included?',
    answer:
      'Yes. Life jackets and the required safety equipment are included with every rental on WetRentals.',
  },
  {
    question: 'Can I bring my own gear, food, or pets?',
    answer:
      'Most hosts welcome your own gear and food. Pet policies vary by listing — check the listing details or message the host before booking.',
  },
  {
    question: 'What should I bring on the day of my rental?',
    answer:
      'Sunscreen, a hat, sunglasses, water, snacks, towels and swimwear. Your host will take care of the boat, fuel options, and any required safety equipment.',
  },
];

const findLocationData = (pathname) => {
  const segments = getLocationSegments(pathname);
  const slug = segments.join('/').toLowerCase();
  return locationsData.find(item => item.Simplified.toLowerCase() === slug) || null;
};

const generateStructuredData = (pageData, pathname, locationName, faqData) => {
  const segments = getLocationSegments(pathname);

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
      ...segments.map((segment, index) => ({
        '@type': 'ListItem',
        position: index + 2,
        name: formatSegment(segment),
        item: `${SITE_URL}/destinations/${segments
          .slice(0, index + 1)
          .join('/')
          .toLowerCase()}/boat-rentals`,
      })),
    ],
  };

  const canonicalUrl = `${SITE_URL}/destinations/${segments.join('/').toLowerCase()}/boat-rentals`;

  const service = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: pageData.Title,
    serviceType: 'Boat Rental',
    description: pageData['SEO Description'],
    provider: {
      '@type': 'Organization',
      name: 'WetRentals',
      url: SITE_URL,
    },
    areaServed: {
      '@type': 'Place',
      name: locationName,
    },
    url: canonicalUrl,
    image: `${SITE_URL}${homeImage}`,
  };

  const faqPage = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return [breadcrumbList, service, faqPage];
};

export const DynamicPageComponent = props => {
  const location = useLocation();
  const pathname = location.pathname || '';
  const pageData = findLocationData(pathname);
  const [openItems, setOpenItems] = useState(new Set());

  if (!pageData) {
    return (
      <Page title="Boat Rentals | WetRentals" scrollingDisabled={false}>
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.loadingContainer}>
            <div className={css.spinner}></div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  const {
    Title: title,
    'Desc 1': description1,
    'Desc 2': description2,
    'Desc 3': description3,
    'SEO Description': seoDesc,
    Link: link,
  } = pageData;

  const formattedLink = link.replace('https://wetrentals.com', '');
  const segments = getLocationSegments(pathname);
  const locationName = segments.length > 0 ? formatSegment(segments[segments.length - 1]) : 'this area';
  const faqData = buildFaqData(locationName);
  const structuredData = generateStructuredData(pageData, pathname, locationName, faqData);

  const toggleItem = index => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  return (
    <Page title={title} description={seoDesc} schema={structuredData} scrollingDisabled={false}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.pageContainer}>
          <Breadcrumb locationSegments={segments} />

          <div className={css.heroSection}>
            <div className={css.heroHeader}>
              <h1 className={css.heroTitle}>{title}</h1>
              <div className={css.heroMeta}>
                <div className={css.heroLocation}>
                  <MapPin className={css.locationIcon} />
                  <span>Trusted local hosts</span>
                </div>
              </div>
            </div>

            <div className={css.heroImageContainer}>
              <img
                src={homeImage}
                alt={`Boat rentals in ${locationName} — WetRentals`}
                className={css.heroImage}
              />
              <div className={css.heroOverlay}></div>
            </div>

            <div className={css.heroContent}>
              <div className={css.featuresGrid}>
                <div className={css.featureItem}>
                  <div className={css.featureIcon}>
                    <Calendar className={css.icon} />
                  </div>
                  <div className={css.featureText}>
                    <h3 className={css.featureTitle}>Flexible Booking</h3>
                    <p className={css.featureDescription}>Easy cancellation</p>
                  </div>
                </div>
                <div className={css.featureItem}>
                  <div className={css.featureIcon}>
                    <Shield className={css.icon} />
                  </div>
                  <div className={css.featureText}>
                    <h3 className={css.featureTitle}>Safety First</h3>
                    <p className={css.featureDescription}>All equipment included</p>
                  </div>
                </div>
                <div className={css.featureItem}>
                  <div className={css.featureIcon}>
                    <Users className={css.icon} />
                  </div>
                  <div className={css.featureText}>
                    <h3 className={css.featureTitle}>Local Hosts</h3>
                    <p className={css.featureDescription}>Real owners, real boats</p>
                  </div>
                </div>
              </div>

              <div className={css.contentWrapper}>
                <p className={css.descriptionLarge}>{description1}</p>
                <p className={css.description}>{description2}</p>
                <p className={css.description}>{description3}</p>
              </div>

              <a href={formattedLink} className={css.ctaButton}>
                Book With WetRentals Now
                <span className={css.ctaArrow}>→</span>
              </a>
            </div>
          </div>

          <div className={css.listingsSection}>
            <DynamicPageListings />
          </div>

          <section className={css.faqSection}>
            <h2 className={css.faqHeading}>Frequently Asked Questions</h2>
            <div className={css.faqContainer}>
              {faqData.map((item, index) => (
                <FAQItem
                  key={index}
                  question={item.question}
                  answer={item.answer}
                  isOpen={openItems.has(index)}
                  onToggle={() => toggleItem(index)}
                />
              ))}
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default compose(injectIntl)(DynamicPageComponent);
